import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { SupplierListRow } from "@/modules/inventory/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon, TruckIcon } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import { formatSupplierApiError } from "../api/suppliers-client";
import {
  useDeactivateSupplier,
  useSuppliersQuery,
} from "../api/suppliers-queries";
import { SUPPLIER_MENU_TITLE } from "../ui/purchase/purchaseLaoLabels";
import {
  SuppliersFilter,
  type SuppliersSearch,
} from "../ui/SuppliersFilter";
import { SuppliersTable } from "../ui/SuppliersTable";

function sortRows(
  rows: SupplierListRow[],
  sortField?: string,
  sortOrder?: "asc" | "desc",
): SupplierListRow[] {
  if (!sortField) return rows;
  const mult = sortOrder === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const aVal = (a as Record<string, unknown>)[sortField];
    const bVal = (b as Record<string, unknown>)[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1 * mult;
    if (bVal == null) return -1 * mult;
    if (typeof aVal === "string" && typeof bVal === "string") {
      return aVal.localeCompare(bVal, "lo") * mult;
    }
    return 0;
  });
}

export function SuppliersPage() {
  const nav = useNavigate({ from: "/app/inventory/suppliers" });
  const search = useSearch({
    from: "/app/inventory/suppliers",
  }) as SuppliersSearch;

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;
  const listParams = {
    search: search.search,
    isActive:
      search.isActive === "true"
        ? true
        : search.isActive === "false"
          ? false
          : undefined,
  };

  const list = useSuppliersQuery(listParams);
  const canManage = useActionPermission(["inventory:purchase:manage"]);
  const deactivate = useDeactivateSupplier();

  const sortedAll = useMemo(
    () => sortRows(list.data ?? [], search.sortField, search.sortOrder),
    [list.data, search.sortField, search.sortOrder],
  );

  const { pageData, totalCount } = useMemo(() => {
    const total = sortedAll.length;
    return {
      pageData: sortedAll.slice(offset, offset + limit),
      totalCount: total,
    };
  }, [sortedAll, offset, limit]);

  const handleDeactivate = async (id: string) => {
    try {
      await deactivate.run(id);
      toast.success("ປິດຜູ້ສະໜອງສຳເລັດ");
    } catch (e) {
      toast.error(formatSupplierApiError(e));
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[{ label: SUPPLIER_MENU_TITLE, to: "/app/inventory/suppliers" }]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <TruckIcon className="size-5" />
              {SUPPLIER_MENU_TITLE}
            </h2>
            <p className="text-muted-foreground">
              ຈັດການຜູ້ສະໜອງສຳລັບໃບສັ່ງຊື້ (PO)
            </p>
          </div>
          {canManage && (
            <Button onClick={() => nav({ to: "/app/inventory/suppliers/create" })}>
              <PlusIcon className="size-4" />
              ເພີ່ມຜູ້ສະໜອງ
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border bg-card">
          <SuppliersFilter />
          <SuppliersTable
            data={pageData}
            isLoading={list.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={(o, l) =>
              nav({ search: { ...search, offset: o, limit: l } })
            }
            sortBy={search.sortField}
            sortOrder={search.sortOrder}
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sortField: id,
                  sortOrder: desc ? "desc" : "asc",
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/inventory/suppliers/$id/edit",
                params: { id: row.id },
              })
            }
            onDeactivate={handleDeactivate}
          />
        </div>
      </Main>
    </>
  );
}
